<template>
  <Dialog
    ref="dialog"
    class="payout-dialog"
    dismissible
    @dialog-closed="payoutTransaction ? $emit('payout-request-sent') : null"
  >
    <div
      v-if="payoutTransaction && payoutAmount"
      class="dialog-body payout-success"
    >
      <lottie-player
        :src="`${publicPath}/assets/78667-confetti.json`"
        background="transparent"
        speed="1"
        style="position: absolute; top: -110px; left: 0; z-index: -1;"
        autoplay
      />
      <lottie-player
        :src="`${publicPath}/assets/97240-success.json`"
        background="transparent"
        speed="1"
        style="width: 180px; height: 180px; margin: 0 auto;"
        autoplay
      />
      <h2
        v-translate
        class="text-blue"
      >
        Payout Successful
      </h2>
      <p
        v-translate
        class="text-body-light"
      >
        Your request has been approved and will be paid out shortly.
      </p>
      <hr>
      <small
        v-translate
        class="text-body-light"
      >Amount requested:</small>
      <p class="amount-requested">
        {{ isUserCurrencyGBP ? formatAmount(payoutTransaction.amount) : `${currencySymbol}${payoutAmount}` }}
      </p>
    </div>

    <div
      v-else
      class="dialog-body"
    >
      <p
        v-translate
        class="text-body-light"
      >
        Your Available Balance
      </p>
      <h2 class="h1 text-blue">
        {{ formatAmount(availableBalance) }}
      </h2>
      <small
        v-if="!isUserCurrencyGBP"
        class="text-body-light"
      >
        {{ $gettext('Amount in GBP:') }} <span class="text-info">{{ formatPrice(availableBalance, 'GBP') }}</span>
      </small>

      <!-- Payout amount input field -->
      <div class="textfield">
        <label
          v-translate
          for="PayoutAmount"
          class="text-body-light"
        >
          Request a payout
        </label>
        <span
          v-if="isUserCurrencyGBP"
          class="prefix"
        >
          &pound;
        </span>
        <span
          v-else
          class="prefix"
        >
          {{ currencySymbol }}
        </span>
        <input
          id="PayoutAmount"
          v-model="payoutAmount"
          type="number"
          :class="{ 'text-error': isAmountInvalid }"
          inputmode="decimal"
          :min="minAmount"
          :max="maxAmount"
          placeholder="0.00"
          step="0.01"
          @keypress="restrictInput($event)"
          @input="checkInputAmount($event)"
          @change="formatInputAmount"
        >
      </div>
      <p>
        <small
          v-if="convertedAmount"
          class="text-info converted-payout-amount"
        >
          {{ $gettext('Amount in GBP') }}:
          <span class="text-info">
            {{ formatPrice(convertedAmount, 'GBP') }}
            ({{ $gettext('approx') }})
            <br>
          </span>
        </small>
        <small
          v-if="error"
          class="text-error"
        >
          <Icon
            name="danger"
            fill="error"
          />
          {{ error }}
        </small>
      </p>
      <div v-if="activePayoutMethods.length && selectedPayoutAccount">
        <DropdownUI
          :key="selectedPayoutAccount?.id"
          :options="payoutMethodDropdownOptions"
          :selected-option-id="selectedPayoutAccount?.id"
          toggle-class="button tertiary"
          toggle-icon="withdraw"
          @selected="selectPayoutAccount"
        />
      </div>
    </div>

    <div
      v-if="!payoutTransaction"
      class="dialog-actions"
    >
      <button
        v-translate
        :disabled="isImpersonateMode || !isWithdrawalEnabled || isSending"
        @click="sendPayoutRequest"
      >
        Withdraw
      </button>
      <small
        class="text-info"
      >
        {{ $gettext('Your payout must be at least £%{ min }.', { min: String(minAmount) }) }}
      </small>
    </div>
  </Dialog>
</template>
<script setup lang="ts">
import { ref, computed } from 'vue'
import { useGettext } from 'vue3-gettext'
import { useStore } from 'vuex'
import Dialog from '@/components/CommonUI/Dialog.vue'
import DropdownUI from '@/components/CommonUI/DropdownUI.vue'
import Icon from '@/components/CommonUI/Icon.vue'
import { AccountTypes, supportedCurrencyConversions } from '@/constants'
import { getErrorMessage } from '@/helpers/errorHandler'
import { formatAmount } from '@/helpers/formatAmount'
import { formatPrice } from '@/helpers/formatPrice'
import { getCurrencySymbol } from '@/helpers/getCurrencySymbol'

const MIN_AMOUNT = 25
const AMOUNT_MINUS_FEE = 0.99

defineEmits<{(e: 'payout-request-sent'): void}>()

const { $gettext } = useGettext()
const store = useStore()

const dialog = ref<InstanceType<typeof Dialog> | null>(null)

const availableBalance = ref(0)
const error = ref<string | null>(null)
const isAmountInvalid = ref(false)
const isSending = ref(false)
const payoutAmount = ref<string | null>(null)
const payoutTransaction = ref<Transaction | null>(null)
const selectedAccountId = ref(0)

const publicPath = process.env.BASE_URL

const userCurrency = computed(() => store.state.userProfile.currency)
const userCurrencyConversionRate = computed(() => store.state.userCurrencyConversionRate)
const currencySymbol = computed(() => getCurrencySymbol(userCurrency.value || 'GBP'))

const activePayoutMethods = computed<PayoutAccount[]>(() => store.getters.activePayoutMethods)
const isImpersonateMode = computed<boolean>(() => store.getters.isImpersonateMode)
const isUserCurrencyGBP = computed<boolean>(() => store.getters.isUserCurrencyGBP)

const payoutMethodDropdownOptions = computed<DropdownOption[]>(
  () => activePayoutMethods.value.map(({ id, name, type }) => ({
    id: Number(id),
    name,
    iconPath:
      type === AccountTypes.payoneer
        ? String(new URL('../assets/payoneer-icon.svg', import.meta.url))
        : type === AccountTypes.tipaltiPaypal
          ? String(new URL('../assets/paypal-icon.svg', import.meta.url))
          : String(new URL('../assets/bank-building.svg', import.meta.url)),
  })),
)

const reverseConversionRate = computed(() => 1 / userCurrencyConversionRate.value)
const preSelectedPayoutMethod = computed(() => activePayoutMethods.value[0])
const selectedPayoutAccount = computed<PayoutAccount | undefined>(() => activePayoutMethods.value.find(
  account => account.id === selectedAccountId.value,
))

const minAmount = computed<number>(() => Number(selectedPayoutAccount.value?.minimumPayoutAmount) || MIN_AMOUNT)
const maxAmount = computed<number>(() => Number((availableBalance.value * userCurrencyConversionRate.value).toFixed(2)))

const isPayoneerSelected = computed<boolean>(() => selectedPayoutAccount.value?.type === AccountTypes.payoneer)
const isWithdrawalEnabled = computed<boolean>(() => Boolean(
  payoutAmount.value
    && Number(payoutAmount.value) >= minAmount.value
    && Number(payoutAmount.value) <= maxAmount.value,
))

const convertedAmount = computed<number | null>(() => {
  if (isUserCurrencyGBP.value) return null

  const baseAmount = Number(payoutAmount.value || 0)

  if (
    selectedPayoutAccount.value
    && !isPayoneerSelected.value
    && supportedCurrencyConversions.includes(selectedPayoutAccount.value.currency)
  ) {
    return baseAmount * AMOUNT_MINUS_FEE * reverseConversionRate.value
  }

  return baseAmount * reverseConversionRate.value
})

const postPayoutRequest = (payload: {
  payoutAmount: string
  payoutAccountId: number
  requestedAmount: string
  requestedCurrency: string
}): Promise<Transaction> => store.dispatch('postPayoutRequest', payload)

const restrictInput = (evt: KeyboardEvent) => {
  const value = (evt.target as HTMLInputElement).value

  if (!/^\d$/.test(evt.key) && evt.key !== '.') {
    evt.preventDefault()
    return
  }

  if (evt.key === '.' && value.includes('.')) {
    evt.preventDefault()
    return
  }

  if (value.includes('.') && value.split('.')[1].length > 1) {
    evt.preventDefault()
  }
}

const checkInputAmount = (evt: Event) => {
  const value = (evt.target as HTMLInputElement).value

  error.value = null
  isAmountInvalid.value = false

  if (!value) return

  if (Number(value) > maxAmount.value) {
    payoutAmount.value = String(maxAmount.value)
  }

  if (Number(value) < minAmount.value) {
    error.value = $gettext('Your payout must be at least £%{ min }.', { min: String(minAmount.value) })
    isAmountInvalid.value = true
  }
}

const formatInputAmount = () => {
  if (!payoutAmount.value) return
  payoutAmount.value = Number(payoutAmount.value).toFixed(2)
}

const hide = () => {
  dialog.value?.hide()
  error.value = null
  payoutTransaction.value = null
}

const show = (balance: number) => {
  availableBalance.value = balance

  if (preSelectedPayoutMethod.value) {
    selectedAccountId.value = preSelectedPayoutMethod.value.id
  }
  dialog.value?.show()

  if (isImpersonateMode.value) {
    error.value = $gettext('Admins are not allowed to make withdrawal requests on behalf of a user')
  }
}

const sendPayoutRequest = async (): Promise<void> => {
  if (!payoutAmount.value || isImpersonateMode.value) return

  isSending.value = true

  try {
    const payoutAmountInGBP = Number(payoutAmount.value) * reverseConversionRate.value

    payoutTransaction.value = await postPayoutRequest({
      payoutAmount: payoutAmountInGBP.toFixed(2),
      payoutAccountId: selectedAccountId.value,
      requestedAmount: payoutAmount.value,
      requestedCurrency: userCurrency.value,
    })
  } catch (err) {
    error.value = getErrorMessage(err)
  }

  isSending.value = false
}

const selectPayoutAccount = (id: number) => {
  selectedAccountId.value = id
}

defineExpose({
  show,
  hide,
})
</script>

<style lang="scss">
.payout-dialog {
  .dialog {
    min-width: fit-content;
  }

  .dialog-actions {
    flex-direction: column;
    align-items: center;
    button {
      width: fit-content;
      margin-bottom: 4px;
    }
  }
  .textfield + p {
    margin-top: 0.5rem;
  }

  .text-error .icon {
    vertical-align: middle;
    margin-right: 0.5rem;
  }

  h2 {
    margin-bottom: 1rem;
  }

  .payout-success {
    p {
      margin: 0 2rem;
    }
    hr {
      margin: 1rem 0;
    }
  }

  .amount-requested {
    font-size: 1.75rem;
    font-weight: 600;
  }
}

.textfield {
  margin-top: 1rem;

  label {
    display: block;
    margin-bottom: 1rem;
  }

  input {
    border: 0;
    border-radius: 1.5rem;
    box-shadow: 0 0 10px 0 rgb(0 0 0 / 20%);
    display: block;
    font-weight: 600;
    font-size: 1.5rem;
    padding: 1rem;
    text-align: center;
    width: 100%;

    &::placeholder {
      color: var(--dark-grey);
      opacity: 1; // Firefox
    }
  }

  .prefix {
    position: absolute;
    left: 4rem;
    padding: 1rem;
    font-size: 1.5rem;
    font-weight: 200;
  }
}

// force font colours for dark-theme in nested dialog
.panel .dialog {
  .text-body-light {
    color: var(--brand-body-light) !important;
  }
  .text-blue {
    color: var(--blue) !important;
  }
  .text-darker-grey {
    color: var(--darker-grey) !important;
  }
}
</style>
